import * as React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { MarkdownBody } from "../scene/MarkdownBody";
import { useContent, service } from "../../hooks/useContent";
import {
  parseBookingReturn,
  stripBookingReturnParams,
} from "./parseBookingReturn";

type Props = {
  /** Service that was booked — resolved against CMS content. */
  serviceKey: string | null;
  open: boolean;
  onClose: () => void;
  portalHost?: HTMLElement | null;
};

/**
 * The booking-confirmation screen itself. Presentational only: real PAID
 * state is set server-side by the Calendly/Stripe webhook, so it never
 * implies a payment on its own.
 */
export function BookingPaidConfirmation({
  serviceKey,
  open,
  onClose,
  portalHost,
}: Props) {
  const { t } = useTranslation();
  const { data: content } = useContent();
  const svc = service(content, serviceKey ?? "");

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={svc ? svc.title.replace(/\n/g, " ") : ""}
      title={t("thankYou")}
      size="compact"
      container={portalHost ?? null}
    >
      <div className="flex flex-col items-center gap-5 py-6 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-[22px] text-primary">
          <Icon name="verified" title={t("thankYou")} />
        </span>
        <div className="max-w-[40ch]">
          <MarkdownBody surface="brand" body={svc?.paymentSuccess ?? ""} />
        </div>
        <Button
          variant="outline"
          surface="light"
          as="button"
          type="button"
          onClick={onClose}
        >
          {t("done")}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * URL-driven wrapper, mounted once at the app root: Stripe's success_url and
 * Calendly's "Redirect to an external site" both land with
 * ?booking_paid=1&service=<key>, and this turns that into the confirmation.
 * Mounting it centrally means every return page is covered with no wiring of
 * its own. The params are stripped once shown, so a reload doesn't re-open it.
 */
export function BookingPaidModal() {
  const [serviceKey, setServiceKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    const key = parseBookingReturn(window.location.search);
    if (!key) return;
    setServiceKey(key);
    window.history.replaceState(
      {},
      "",
      stripBookingReturnParams(window.location.href),
    );
  }, []);

  return (
    <BookingPaidConfirmation
      serviceKey={serviceKey}
      open={serviceKey !== null}
      onClose={() => setServiceKey(null)}
    />
  );
}
